import { Injectable, signal, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, tap, map, catchError, of } from 'rxjs';
import { environment } from '../../../environments/environment';
import { Project } from '../models/project.model';
import { AuthenticationService } from './Authentication/authentication.service';
import { ToastService } from './toast.service';

@Injectable({
  providedIn: 'root',
})
export class ProjectService {
  private readonly API_URL = `${environment.apiUrl}/projects`;
  private readonly http = inject(HttpClient);
  private readonly authService = inject(AuthenticationService);
  private readonly toastService = inject(ToastService);

  readonly projects = signal<Project[]>([]);

  constructor() {
    if (this.authService.getAccessToken()) {
      this.loadProjects().subscribe();
    }
  }

  loadProjects(departmentId?: string | number): Observable<Project[]> {
    if (!this.authService.getAccessToken()) {
      return of([]);
    }

    const url = departmentId ? `${this.API_URL}?departmentId=${departmentId}` : this.API_URL;
    return this.http.get<any>(url).pipe(
      map((res) => {
        let items: any[] = [];
        if (res.items) items = res.items;
        else if (res.data?.items) items = res.data.items;
        else if (res.data && Array.isArray(res.data)) items = res.data;
        else if (Array.isArray(res)) items = res;

        return items.map((p: any) => ({
          ...p,
          id: Number(p.id),
          departmentId: Number(p.departmentId || p.department?.id || 0),
          departmentName: p.department?.name || '',
          memberUserIds: p.members ? p.members.map((m: any) => Number(m.id)) : [],
          members: p.members || [],
          status: p.status || 'Active',
        }));
      }),
      tap((projs: Project[]) => this.projects.set(projs)),
      catchError(() => of([])),
    );
  }

  addProject(dto: {
    name: string;
    code: string;
    description?: string;
    departmentId: number;
    defaultAssigneeId?: number;
    memberUserIds?: number[];
  }): Observable<Project> {
    return this.http.post<any>(this.API_URL, dto).pipe(
      map((res) => (res.data ? res.data : res)),
      tap(() => {
        this.loadProjects().subscribe();
        this.toastService.success('Project Created Successfully');
      }),
    );
  }

  updateProject(id: string | number, dto: Partial<Project>): Observable<Project> {
    return this.http.patch<any>(`${this.API_URL}/${id}`, dto).pipe(
      map((res) => (res.data ? res.data : res)),
      tap(() => {
        this.loadProjects().subscribe();
        this.toastService.success('Project Updated Successfully');
      }),
    );
  }

  deleteProject(id: string | number): Observable<void> {
    return this.http.delete<void>(`${this.API_URL}/${id}`).pipe(
      tap(() => {
        this.loadProjects().subscribe();
        this.toastService.info('Project Deleted');
      }),
    );
  }
}
